import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Info, Plus, Check, Star, Tv } from 'lucide-react';
import { subtitleFor } from '../../lib/catalog';
import { BACKDROP_SIZES, backdropSet, backdropSrc } from '../../lib/images';

const DWELL_MS = 9000;

/**
 * Hero d'ouverture.
 *
 * Un fondu enchaîné entre affiches, un lent travelling (Ken Burns) et une
 * pagination qui se remplit au rythme de l'attente. Le défilement s'arrête dès
 * que le pointeur entre, que le doigt touche, ou que l'onglet passe en
 * arrière-plan — un carrousel qui tourne dans le vide consomme de la batterie
 * pour rien.
 */
export default function Hero({ titles, onOpen, onToggleList, isInList }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);

  const count = titles.length;

  const go = useCallback((next) => setActive(((next % count) + count) % count), [count]);

  // Rotation automatique
  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const timer = setTimeout(() => go(active + 1), DWELL_MS);
    return () => clearTimeout(timer);
  }, [active, paused, count, go]);

  // Onglet masqué : on suspend, rien ne sert d'animer hors de l'écran.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  if (!count) return null;

  const title = titles[active];
  const isSeries = title.isGroupedSeries || title.type === 'serie';
  const inList = isInList ? isInList(title) : false;

  const onTouchStart = (event) => {
    touchStart.current = event.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    setPaused(false);
    if (start == null) return;
    const delta = event.changedTouches[0].clientX - start;
    // 48 px : au-delà, l'intention de balayer ne fait plus de doute.
    if (Math.abs(delta) > 48) go(active + (delta < 0 ? 1 : -1));
  };

  return (
    <section
      className="hero"
      aria-roledescription="carrousel"
      aria-label="À l'affiche"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="hero-canvas" aria-hidden="true">
        {titles.map((item, index) => (
          <div key={item.id} className={`hero-slide${index === active ? ' is-live' : ''}`}>
            {(item.backdrop || item.poster) && (
              <img
                src={backdropSrc(item.backdrop || item.poster)}
                srcSet={backdropSet(item.backdrop || item.poster)}
                sizes={BACKDROP_SIZES}
                alt=""
                // La première image est critique pour le LCP : elle doit être
                // demandée tout de suite, les suivantes peuvent attendre.
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'low'}
                decoding="async"
              />
            )}
          </div>
        ))}
      </div>
      <div className="hero-veil" aria-hidden="true" />

      <div className="hero-body">
        <div className="hero-inner" key={title.id}>
          <div className="hero-eyebrow anim-rise">
            <span className="badge badge-gold">À l'affiche</span>
            {isSeries && (
              <span className="badge badge-neutral">
                <Tv className="w-[11px] h-[11px]" aria-hidden="true" />
                Série
              </span>
            )}
          </div>

          <h1 className="hero-title anim-rise d-1">{title.title}</h1>

          <div className="hero-facts anim-rise d-2">
            {Number(title.rating) > 0 && (
              <span className="score">
                <Star aria-hidden="true" />
                {Number(title.rating).toFixed(1)}
              </span>
            )}
            {title.year && <span>{title.year}</span>}
            <span className="dot" aria-hidden="true" />
            <span>{subtitleFor(title)}</span>
            {title.genres?.length > 0 && (
              <>
                <span className="dot" aria-hidden="true" />
                <span>{title.genres.slice(0, 3).join(' · ')}</span>
              </>
            )}
          </div>

          {title.overview && <p className="hero-pitch clamp-3 anim-rise d-3">{title.overview}</p>}

          <div className="hero-actions anim-rise d-4">
            <button type="button" className="btn btn-lg btn-light" onClick={() => onOpen(title)}>
              <Play aria-hidden="true" style={{ fill: 'currentColor' }} />
              Lire
            </button>
            {onToggleList && (
              <button
                type="button"
                className="btn btn-lg btn-glass"
                aria-pressed={inList}
                onClick={() => onToggleList(title)}
              >
                {inList ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
                {inList ? 'Dans ma liste' : 'Ma liste'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-lg btn-ghost"
              onClick={() => onOpen(title)}
              aria-label={`Plus d'informations sur ${title.title}`}
            >
              <Info aria-hidden="true" />
              <span className="hidden sm:inline">Détails</span>
            </button>
          </div>

          {count > 1 && (
            <div className="hero-dots" role="tablist" aria-label="Choisir un titre à la une">
              {titles.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={index === active}
                  aria-label={item.title}
                  className={`hero-dot${index === active ? ' is-live' : ''}`}
                  style={{ '--hero-dwell': `${DWELL_MS}ms` }}
                  onClick={() => setActive(index)}
                >
                  {/* La jauge suit la pause : elle ne doit pas se remplir
                      pendant que le survol retient le carrousel. */}
                  <i style={{ animationPlayState: paused ? 'paused' : 'running' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
